import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import getCdkSidFromOkta from './getCdkSidFromOkta';

export interface State {
  iamUserProfile: any;
  unifyUserProfile: any;
  oktaUserProfile: any;
  theme: any;
  locale:string,
  enterpriseId:string,
  appView:string,
  isEnterpriseAdmin: boolean;
  roles: string[];
  permissions:string[];
  email:string;
  login:string;
  currentStoreId:string;
  currentStore:any;
  aivaAccess:boolean;
  setIamUserProfile: (iamUserProfile: any) => void;
  setUnifyUserProfile: (unifyUserProfile: any) => void;
  setOktaUserProfile: (oktaUserProfile: any) => void;
  setTheme: (theme: any) => void;
  setLocale :(locale:string) => void;
  setAppView: (appView: string) => void;
  setAivaAccess: (aivaAccess: boolean) => void;
}

const permissions = (entitlements:any) =>{
  return entitlements?.flatMap((item:any) => {
    const parsed = JSON.parse(item);
    const key = Object.keys(parsed)[0];
    const values = parsed[key];
    // Build combined list: parent + parent.child
    const nested = Array.isArray(values)
      ? values.map((v) => `${key}.${v}`)
      : [];
    return [key, ...nested];
  }) ?? [];
}

const _setOktaUserProfile = (set: any) => (oktaUserProfile: any) =>
  set(() => {
    if(!!oktaUserProfile.userProfile){
      const { userProfile, ...rest } = oktaUserProfile;
      const updated = {
        ...rest,
        profile: userProfile,
      };
      return {
        oktaUserProfile: {...updated},
        locale: updated?.locale,
        email: updated?.profile?.email,
        login: updated?.profile?.login,
        permissions: permissions(updated?.profile?.entitlements)
      };
    }else{
      return {
        oktaUserProfile,
        locale: oktaUserProfile?.profile?.locale,
        email: oktaUserProfile?.profile?.email,
        login: oktaUserProfile?.profile?.login,
        permissions: permissions(oktaUserProfile?.profile?.entitlements)
      }
    }
  });

const _isEnterpriseAdmin = (iamUserProfile: any) => {
  return (
    iamUserProfile?.fullUser?.roles?.some((role: any) => 
      role.roleName.toLowerCase().includes("enterprise administrator")
    ) ?? false
  );
};


const _currentStore = (iamUserProfile: any) => {
  return iamUserProfile?.fullUser?.stores?.filter(
      (store:any) => store.defaultStore
    );
};

const _setIamUserProfile = (set: any) => (iamUserProfile: any) =>
  set(() => {
    return {
      iamUserProfile,
      isEnterpriseAdmin: _isEnterpriseAdmin(iamUserProfile),
      roles: iamUserProfile?.fullUser?.roles?.map((role: any) => role.roleName?.toLowerCase()).filter(Boolean) ?? [],
      currentStore: _currentStore(iamUserProfile),
      currentStoreId: _currentStore(iamUserProfile)[0]?.id,
      enterpriseId: iamUserProfile?.fullUser ? iamUserProfile?.fullUser?.enterprise?.id  : !getCdkSidFromOkta() ? "Spoke Users" : null
    }
  });
const _setUnifyUserProfile = (set: any) => (unifyUserProfile: any) =>
  set(() => {
    return {
      unifyUserProfile,
      appView: unifyUserProfile?.appView || 'default',
      theme: unifyUserProfile?.appTheme,
    };
  });

const userProfileStore = create<State>((set) => ({
  iamUserProfile: {},
  unifyUserProfile: {},
  oktaUserProfile: {},
  theme: localStorage.getItem('unify-theme') || "light",
  locale: "en_US",
  enterpriseId:"",
  appView:"default",
  isEnterpriseAdmin: false,
  isOfficeManagerRole: false,
  isDealerExecutiveRole: false,
  isPredianAdminRole: false,
  roles: [],
  permissions:[],
  currentStore:{},
  currentStoreId:"",
  email:"",
  login:"",
  aivaAccess: false,
  setIamUserProfile: _setIamUserProfile(set),
  setUnifyUserProfile: _setUnifyUserProfile(set),
  setOktaUserProfile: _setOktaUserProfile(set),
  setTheme: (theme: string) => set(() => ({ theme })),
  setLocale: (locale: string) => set(() => ({ locale })),
  setAppView: (appView: string) => set(() => ({ appView })),
  setAivaAccess: (aivaAccess: boolean) => set(() => ({ aivaAccess })),
}));

export interface LastLoginState {
  loggedInDate: any;
  setLoggedInDate: (date: any) => void;
}


export const useUserLastLoggedInStore = create<any>(
  persist(
    (set) => ({
      loggedInDate: undefined,
      setLoggedInDate: (loggedInDate:string) => set({ loggedInDate}),
    }),
    {
      name: "unify-user-last-loggedin", // Local Storage Key
      storage: createJSONStorage(() => localStorage), // Define Storage Type
    }
  )
);

export default userProfileStore;
